import React from "react";
import { motion } from "framer-motion";
import {
  Coffee,
  ShieldCheck,
  PenTool,
  Cpu,
  ArrowUpRight,
  Tag,
  Download,
} from "lucide-react";
import { BROCHURES } from "@/lib/brand";

const machines = [
  {
    icon: Coffee,
    title: "Snacks & Beverage Vending Machines",
    desc:
      "Flagship Refuel CORE, LITE & MAX — HD touch, UPI/cards, precision cooling, up to 300 SKUs.",
    bullets: ["Up to 300 items", "UPI / Card / Wallet", "Cloud telemetry"],
    tag: "Best Seller",
    accent: "emerald",
    brochures: [BROCHURES.core, BROCHURES.lite, BROCHURES.max],
  },
  {
    icon: ShieldCheck,
    title: "PPE & Safety Vending Machines",
    desc:
      "Refuel SAFE dispenses gloves, masks, glasses and consumables. Auditable issuance for plants, hospitals and labs.",
    bullets: ["Smart authentication", "Sure-Vend tech", "Audit logs"],
    tag: "For Plants & Hospitals",
    accent: "amber",
    brochures: [BROCHURES.safe],
  },
  {
    icon: PenTool,
    title: "Stationery Vending Machines",
    desc:
      "Office supplies and lab consumables on the floor. Cuts shrinkage and admin overhead.",
    bullets: ["Custom planograms", "Per-employee limits", "Lockable bays"],
    tag: "Custom Builds",
    accent: "emerald",
    brochures: [],
  },
  {
    icon: Cpu,
    title: "Smart Software Stack",
    desc:
      "Every machine ships cashless-ready with UPI, telemetry, stock alerts and dashboards via our partner network.",
    bullets: ["UPI + cards + wallets", "Remote diagnostics", "Inventory dashboards"],
    tag: "Included",
    accent: "amber",
    brochures: [],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function Machines() {
  return (
    <section
      id="machines"
      data-testid="what-we-do-section"
      className="relative py-24 md:py-32 bg-zinc-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-600 mb-3">
              Vending Machines For Sale
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950">
              Built to earn back.
              <br />
              <span className="text-zinc-500">Built to last.</span>
            </h2>
          </div>
          <p className="text-zinc-600 max-w-md text-sm md:text-base">
            Snacks, beverages, PPE and stationery — pick a vending machine model
            below or talk to us for a custom build.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {machines.map((m, i) => {
            const Icon = m.icon;
            const accentText = m.accent === "emerald" ? "text-emerald-600" : "text-amber-600";
            const accentBg = m.accent === "emerald" ? "bg-emerald-50" : "bg-amber-50";
            const accentBorder =
              m.accent === "emerald"
                ? "hover:border-emerald-400"
                : "hover:border-amber-400";
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`whatwedo-card-${i}`}
                className={`group relative overflow-hidden rounded-3xl border border-zinc-200 bg-white ${accentBorder} transition-all p-7 md:p-8 hover:shadow-xl hover:shadow-zinc-900/5 hover:-translate-y-1`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div
                    className={`w-12 h-12 rounded-2xl ${accentBg} flex items-center justify-center ${accentText}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1">
                    <Tag className="w-3 h-3" />
                    {m.tag}
                  </span>
                </div>

                <h3 className="font-display text-2xl md:text-3xl font-semibold text-zinc-950 tracking-tight">
                  {m.title}
                </h3>
                <p className="mt-3 text-zinc-600 text-sm md:text-base">{m.desc}</p>

                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {m.bullets.map((b) => (
                    <li
                      key={b}
                      className="text-xs md:text-[13px] text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2"
                    >
                      {b}
                    </li>
                  ))}
                </ul>

                {m.brochures && m.brochures.length > 0 && (
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mr-1">
                      Brochures
                    </span>
                    {m.brochures.map((b) => (
                      <a
                        key={b.url}
                        href={b.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        data-testid={`brochure-${slug(b.name)}`}
                        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-zinc-900 bg-zinc-100 hover:bg-emerald-50 hover:text-emerald-700 border border-zinc-200 hover:border-emerald-300 rounded-full px-3 py-1.5 transition"
                      >
                        <Download className="w-3.5 h-3.5" />
                        {b.name}
                      </a>
                    ))}
                  </div>
                )}

                <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-zinc-900 group-hover:text-emerald-700 transition">
                  Request a quote <ArrowUpRight className="w-4 h-4" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Secondary option: rental — corporate only */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="mt-10 rounded-3xl border border-dashed border-zinc-300 bg-white p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          data-testid="rental-note"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
              Secondary option
            </div>
            <div className="font-display text-lg md:text-xl font-semibold text-zinc-950">
              Prefer not to buy? Rentals available for corporate customers only.
            </div>
            <div className="text-sm text-zinc-600 mt-1">
              Subject to site survey and minimum-tenure agreement.
            </div>
          </div>
          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-full px-3 py-1.5 uppercase tracking-[0.18em] whitespace-nowrap">
            Corporate only
          </div>
        </motion.div>
      </div>
    </section>
  );
}
